import React, { useEffect, useRef, useState } from "react";
import {
  TextField,
  Grid,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Switch,
  Dialog,
  DialogContent,
  Divider,
  Pagination,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ImageIcon from "@mui/icons-material/Image";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Swal from "sweetalert2";
import {
  createSeccion,
  deleteSeccion,
  getCurso,
  getSeccionesCurso,
  getSeccionPdfDownloadUrl,
  updateCurso,
  updateSeccion,
  uploadCursoPortada,
} from "../services/cursosService";
import { deletePregunta, getPreguntas } from "../services/preguntasService";
import { useResolvableImageSrc } from "../hooks/useResolvableImageSrc";
import AddCourseQuestionsForm from "./AddCourseQuestionsForm";

const PORTADA_MAX_BYTES = 5 * 1024 * 1024;
const PORTADA_MIME = ["image/jpeg", "image/png", "image/webp", "image/gif"];

const EditCourseForm = ({ onSuccess, initialCourseId = "" }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const PREGUNTAS_PAGE_SIZE = 10;
  const getApiErrorMessage = (error, fallback) =>
    error?.response?.data?.messages?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallback;

  const [idCurso, setIdCurso] = useState("");
  const [loadingBuscar, setLoadingBuscar] = useState(false);
  const [loadingGuardar, setLoadingGuardar] = useState(false);
  const [loadingSeccion, setLoadingSeccion] = useState(false);
  const [cursoCargado, setCursoCargado] = useState(false);
  const [step, setStep] = useState(1);
  const [preview, setPreview] = useState("");
  const [fileName, setFileName] = useState("");
  const [coverFile, setCoverFile] = useState(null);
  const [removedCover, setRemovedCover] = useState(false);
  const previewObjectUrlRef = useRef("");
  const [formData, setFormData] = useState({
    Nombre: "",
    Descripcion: "",
    ImagenPortada: "",
    Estado: 1,
    PorcentajeAprobacion: 70,
    CantidadPreguntas: 10,
  });
  const [secciones, setSecciones] = useState([]);
  const [preguntas, setPreguntas] = useState([]);
  const [preguntaModalOpen, setPreguntaModalOpen] = useState(false);
  const [preguntaSeleccionada, setPreguntaSeleccionada] = useState(null);
  const [loadingPreguntas, setLoadingPreguntas] = useState(false);
  const [preguntasSearch, setPreguntasSearch] = useState("");
  const [soloPreguntasActivas, setSoloPreguntasActivas] = useState(true);
  const [preguntasPage, setPreguntasPage] = useState(1);
  const [seccionForm, setSeccionForm] = useState({
    idSeccion: null,
    nombre: "",
    orden: 1,
    estado: 1,
    archivo: null,
    archivoNombre: "",
    archivoUrl: "",
  });
  const coverDisplaySrc = useResolvableImageSrc(preview || formData.ImagenPortada);

  const revokeLocalPreviewUrl = () => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = "";
    }
  };

  useEffect(() => () => revokeLocalPreviewUrl(), []);

  const resetForm = () => {
    setIdCurso("");
    setCursoCargado(false);
    revokeLocalPreviewUrl();
    setCoverFile(null);
    setRemovedCover(false);
    setPreview("");
    setFileName("");
    setFormData({
      Nombre: "",
      Descripcion: "",
      ImagenPortada: "",
      Estado: 1,
      PorcentajeAprobacion: 70,
      CantidadPreguntas: 10,
    });
    setSecciones([]);
    setPreguntas([]);
    setStep(1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const parsedValue =
      name === "Estado" || name === "PorcentajeAprobacion" || name === "CantidadPreguntas"
        ? parseInt(value, 10)
        : value;
    setFormData((prev) => ({
      ...prev,
      [name]: parsedValue,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!PORTADA_MIME.includes(file.type)) {
      Swal.fire("Error", "Formato no permitido. Usa JPEG, PNG, WebP o GIF.", "error");
      e.target.value = "";
      return;
    }
    if (file.size > PORTADA_MAX_BYTES) {
      Swal.fire("Error", "La imagen supera el máximo permitido (5 MB).", "error");
      e.target.value = "";
      return;
    }

    revokeLocalPreviewUrl();
    const objectUrl = URL.createObjectURL(file);
    previewObjectUrlRef.current = objectUrl;
    setCoverFile(file);
    setRemovedCover(false);
    setFileName(file.name);
    setPreview(objectUrl);
    e.target.value = "";
  };

  const normalizarCurso = (response) => {
    const data = response?.data ?? response;
    const rawSecciones = data?.Secciones ?? data?.secciones ?? [];
    const rawPreguntas = data?.Preguntas ?? data?.preguntas ?? [];

    const resolveRutaArchivo = (seccion) =>
      seccion?.RutaArchivo ??
      seccion?.rutaArchivo ??
      seccion?.ArchivoUrl ??
      seccion?.archivoUrl ??
      seccion?.UrlArchivo ??
      seccion?.urlArchivo ??
      seccion?.Archivo ??
      seccion?.archivo ??
      seccion?.archivoNombre ??
      "";

    const resolveNombreArchivo = (seccion) =>
      resolveRutaArchivo(seccion)
        .toString()
        .split("/")
        .pop()
        .split("\\")
        .pop() || "";

    const parsedSecciones = Array.isArray(rawSecciones)
      ? rawSecciones.map((s, index) => ({
          archivoRaw: resolveRutaArchivo(s),
          idSeccion:
            s?.idSeccionCurso ??
            s?.IdSeccionCurso ??
            s?.idSeccion ??
            s?.IdSeccion ??
            s?.id ??
            null,
          nombre: s?.Nombre ?? s?.nombre ?? "",
          orden: Number(s?.Orden ?? s?.orden ?? index + 1) || index + 1,
          estado: Number(s?.Estado ?? s?.estado ?? 1),
          archivoNombre: resolveNombreArchivo(s),
          archivoUrl: resolveRutaArchivo(s),
          archivo: null,
        }))
      : [];

    const parsedPreguntas = Array.isArray(rawPreguntas)
      ? rawPreguntas.map((p) => ({
          idPregunta: p?.idPregunta ?? p?.IdPregunta ?? p?.id ?? null,
          idSeccion: p?.idSeccion ?? p?.IdSeccion ?? null,
          pregunta: p?.Pregunta ?? p?.pregunta ?? "",
          descripcion: p?.Descripcion ?? p?.descripcion ?? "",
          tipo: p?.Tipo ?? p?.tipo ?? "multiple",
          opciones: Array.isArray(p?.Opciones ?? p?.opciones)
            ? (p?.Opciones ?? p?.opciones).map((o) => ({
                respuesta: o?.Respuesta ?? o?.respuesta ?? "",
                correcta: o?.Correcta === true || o?.Correcta === "1" || o?.correcta === true,
              }))
            : [],
          rango: Array.isArray(p?.Rango ?? p?.rango)
            ? (p?.Rango ?? p?.rango)
            : [Number(p?.RangoMin ?? p?.rangoMin ?? 0), Number(p?.RangoMax ?? p?.rangoMax ?? 100)],
          estado: Number(p?.Estado ?? p?.estado ?? 1),
        }))
      : [];

    return {
      id:
        data?.idCurso ??
        data?.id ??
        data?.IdCurso ??
        response?.idCurso ??
        response?.id,
      Nombre: data?.Nombre ?? data?.nombre ?? "",
      Descripcion: data?.Descripcion ?? data?.descripcion ?? "",
      ImagenPortada: data?.ImagenPortada ?? data?.imagenPortada ?? "",
      Estado:
        typeof (data?.Estado ?? data?.estado) === "string"
          ? parseInt(data?.Estado ?? data?.estado, 10)
          : (data?.Estado ?? data?.estado ?? 1),
      PorcentajeAprobacion:
        typeof (data?.PorcentajeAprobacion ?? data?.porcentajeAprobacion) === "string"
          ? parseInt(data?.PorcentajeAprobacion ?? data?.porcentajeAprobacion, 10)
          : (data?.PorcentajeAprobacion ?? data?.porcentajeAprobacion ?? 70),
      CantidadPreguntas:
        typeof (data?.CantidadPreguntas ?? data?.cantidadPreguntas) === "string"
          ? parseInt(data?.CantidadPreguntas ?? data?.cantidadPreguntas, 10)
          : (data?.CantidadPreguntas ?? data?.cantidadPreguntas ?? 10),
      secciones: parsedSecciones,
      preguntas: parsedPreguntas,
    };
  };

  const normalizarSecciones = (response) => {
    const data = response?.data ?? response;
    const list = Array.isArray(data) ? data : data?.Secciones ?? data?.secciones ?? [];

    if (!Array.isArray(list)) return [];

    const resolveRutaArchivo = (seccion) =>
      seccion?.RutaArchivo ??
      seccion?.rutaArchivo ??
      seccion?.ArchivoUrl ??
      seccion?.archivoUrl ??
      seccion?.UrlArchivo ??
      seccion?.urlArchivo ??
      seccion?.Archivo ??
      seccion?.archivo ??
      seccion?.archivoNombre ??
      "";

    const resolveNombreArchivo = (seccion) =>
      resolveRutaArchivo(seccion)
        .toString()
        .split("/")
        .pop()
        .split("\\")
        .pop() || "";

    return list.map((s, index) => ({
      archivoRaw: resolveRutaArchivo(s),
      idSeccion:
        s?.idSeccionCurso ??
        s?.IdSeccionCurso ??
        s?.idSeccion ??
        s?.IdSeccion ??
        s?.id ??
        null,
      nombre: s?.Nombre ?? s?.nombre ?? "",
      orden: Number(s?.Orden ?? s?.orden ?? index + 1) || index + 1,
      estado: Number(s?.Estado ?? s?.estado ?? 1),
      archivoNombre: resolveNombreArchivo(s),
      archivoUrl: resolveRutaArchivo(s),
      archivo: null,
    }));
  };

  const normalizarPreguntas = (response) => {
    const data = response?.data ?? response;
    const list = Array.isArray(data) ? data : data?.preguntas ?? data?.Preguntas ?? [];
    if (!Array.isArray(list)) return [];

    return list.map((p) => ({
      idPregunta: p?.idPregunta ?? p?.IdPregunta ?? p?.id ?? null,
      idSeccion: p?.idSeccion ?? p?.IdSeccion ?? p?.idSeccionCurso ?? null,
      pregunta: p?.Pregunta ?? p?.pregunta ?? p?.Texto ?? "",
      descripcion: p?.Descripcion ?? p?.descripcion ?? "",
      tipo: p?.Tipo ?? p?.tipo ?? "multiple",
      opciones: Array.isArray(p?.Opciones ?? p?.opciones)
        ? (p?.Opciones ?? p?.opciones).map((o) => ({
            respuesta: o?.Respuesta ?? o?.respuesta ?? o?.Texto ?? "",
            correcta:
              o?.Correcta === true ||
              o?.correcta === true ||
              o?.EsCorrecta === true ||
              o?.Correcta === "1",
          }))
        : [],
      rango: Array.isArray(p?.Rango ?? p?.rango)
        ? (p?.Rango ?? p?.rango)
        : [Number(p?.RangoMin ?? p?.rangoMin ?? 0), Number(p?.RangoMax ?? p?.rangoMax ?? 100)],
      estado: Number(p?.Estado ?? p?.estado ?? 1),
    }));
  };

  const cargarPreguntasCurso = async (cursoId) => {
    const response = await getPreguntas({ idCurso: Number(cursoId) });
    const parsed = normalizarPreguntas(response);
    setPreguntas(parsed);
  };

  const resolveFileUrl = (value) => getSeccionPdfDownloadUrl(idCurso, value);

  const handleBuscarCurso = async () => {
    const id = String(idCurso).trim();
    if (!id) {
      Swal.fire("Error", "Debes ingresar el ID del curso.", "error");
      return;
    }

    try {
      setLoadingBuscar(true);
      setLoadingPreguntas(true);
      const [cursoResponse, seccionesResponse] = await Promise.all([
        getCurso(id),
        getSeccionesCurso(id),
      ]);
      const curso = normalizarCurso(cursoResponse);
      const seccionesFromEndpoint = normalizarSecciones(seccionesResponse);

      if (!curso?.id && curso.id !== 0) {
        Swal.fire("Error", "No se pudo obtener la información del curso.", "error");
        return;
      }

      setIdCurso(String(curso.id));
      revokeLocalPreviewUrl();
      setCoverFile(null);
      setRemovedCover(false);
      setFormData({
        Nombre: curso.Nombre,
        Descripcion: curso.Descripcion,
        ImagenPortada: curso.ImagenPortada || "",
        Estado: Number.isNaN(curso.Estado) ? 1 : curso.Estado,
        PorcentajeAprobacion: Number.isNaN(curso.PorcentajeAprobacion) ? 70 : curso.PorcentajeAprobacion,
        CantidadPreguntas: Number.isNaN(curso.CantidadPreguntas) ? 10 : curso.CantidadPreguntas,
      });
      setPreview(curso.ImagenPortada || "");
      setFileName("");
      setSecciones(seccionesFromEndpoint.length > 0 ? seccionesFromEndpoint : curso.secciones);
      await cargarPreguntasCurso(curso.id);
      setCursoCargado(true);
      Swal.fire("Listo", "Curso cargado correctamente.", "success");
    } catch (error) {
      console.error("Error al cargar curso:", error);
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "No se pudo cargar el curso.";
      Swal.fire("Error", msg, "error");
      setCursoCargado(false);
    } finally {
      setLoadingPreguntas(false);
      setLoadingBuscar(false);
    }
  };

  const recargarSecciones = async (cursoId) => {
    const response = await getSeccionesCurso(cursoId);
    const parsed = normalizarSecciones(response);
    setSecciones(parsed);
  };

  const resetSeccionForm = () => {
    setSeccionForm({
      idSeccion: null,
      nombre: "",
      orden: secciones.length + 1,
      estado: 1,
      archivo: null,
      archivoNombre: "",
      archivoUrl: "",
    });
  };

  const handleGuardarSeccion = async () => {
    if (!idCurso) {
      Swal.fire("Error", "No se encontró el curso a editar.", "error");
      return;
    }
    if (!seccionForm.nombre.trim()) {
      Swal.fire("Error", "El nombre de la sección es obligatorio.", "error");
      return;
    }

    const hasArchivoActual =
      !!seccionForm.archivo ||
      !!String(seccionForm.archivoNombre || "").trim() ||
      !!String(seccionForm.archivoUrl || "").trim();
    if (!hasArchivoActual) {
      Swal.fire("Error", "Debes seleccionar un PDF para la sección.", "error");
      return;
    }

    try {
      setLoadingSeccion(true);
      const seccionId = seccionForm.idSeccion ?? null;
      if (seccionId) {
        await updateSeccion(idCurso, seccionId, {
          nombre: seccionForm.nombre.trim(),
          orden: seccionForm.orden,
          estado: seccionForm.estado,
          archivo: seccionForm.archivo || null,
        });
        Swal.fire("Actualizado", "Sección editada correctamente.", "success");
      } else {
        await createSeccion(idCurso, {
          nombre: seccionForm.nombre.trim(),
          orden: seccionForm.orden,
          estado: seccionForm.estado,
          archivo: seccionForm.archivo,
        });
        Swal.fire("Creada", "Sección agregada correctamente.", "success");
      }

      await recargarSecciones(idCurso);
      resetSeccionForm();
    } catch (error) {
      Swal.fire(
        "Error",
        getApiErrorMessage(error, "No se pudo guardar la sección."),
        "error"
      );
    } finally {
      setLoadingSeccion(false);
    }
  };

  const handleEliminarSeccion = async (sec) => {
    if (!idCurso) return;

    const confirm = await Swal.fire({
      title: "Eliminar sección",
      text: `¿Deseas eliminar la sección "${sec.nombre}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!confirm.isConfirmed) return;

    try {
      setLoadingSeccion(true);
      const seccionId =
        sec?.idSeccion ??
        sec?.idSeccionCurso ??
        sec?.IdSeccionCurso ??
        sec?.id ??
        sec?.Id ??
        null;
      if (!seccionId) {
        throw new Error("La sección no tiene idSeccionCurso válido para eliminar.");
      }
      await deleteSeccion(idCurso, seccionId);
      await recargarSecciones(idCurso);
      Swal.fire("Eliminada", "Sección eliminada correctamente.", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        getApiErrorMessage(error, "No se pudo eliminar la sección."),
        "error"
      );
    } finally {
      setLoadingSeccion(false);
    }
  };

  useEffect(() => {
    if (!initialCourseId) return;
    setIdCurso(String(initialCourseId));
  }, [initialCourseId]);

  useEffect(() => {
    if (!initialCourseId || !idCurso) return;
    handleBuscarCurso();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCourseId, idCurso]);

  const handleGuardarCambios = async (e) => {
    e?.preventDefault?.();

    if (!idCurso) {
      Swal.fire("Error", "Primero debes cargar un curso por ID.", "error");
      return;
    }
    if (!formData.Nombre.trim()) {
      Swal.fire("Error", "El nombre del curso es obligatorio.", "error");
      return;
    }
    if (!formData.Descripcion.trim()) {
      Swal.fire("Error", "La descripción del curso es obligatoria.", "error");
      return;
    }
    if (!Number.isInteger(formData.PorcentajeAprobacion) || formData.PorcentajeAprobacion < 1 || formData.PorcentajeAprobacion > 100) {
      Swal.fire("Error", "El porcentaje de aprobación debe estar entre 1 y 100.", "error");
      return;
    }
    if (!Number.isInteger(formData.CantidadPreguntas) || formData.CantidadPreguntas < 1 || formData.CantidadPreguntas > 25) {
      Swal.fire("Error", "La cantidad de preguntas debe estar entre 1 y 25.", "error");
      return;
    }

    try {
      setLoadingGuardar(true);
      const payload = {
        Nombre: formData.Nombre.trim(),
        Descripcion: formData.Descripcion.trim(),
        Estado: formData.Estado,
        PorcentajeAprobacion: formData.PorcentajeAprobacion,
        CantidadPreguntas: formData.CantidadPreguntas,
      };
      if (removedCover && !coverFile) {
        payload.ImagenPortada = null;
      }
      await updateCurso(idCurso, payload);
      if (coverFile) {
        const res = await uploadCursoPortada(idCurso, coverFile);
        const newPath = res?.ImagenPortada;
        if (newPath) {
          setFormData((prev) => ({ ...prev, ImagenPortada: newPath }));
          setPreview(newPath);
        }
        revokeLocalPreviewUrl();
        setCoverFile(null);
        setRemovedCover(false);
        setFileName("");
      } else if (removedCover) {
        setRemovedCover(false);
      }
      Swal.fire("Actualizado", "Curso actualizado correctamente.", "success");
      onSuccess?.();
    } catch (error) {
      console.error("Error al actualizar curso:", error);
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "No se pudo actualizar el curso.";
      Swal.fire("Error", msg, "error");
    } finally {
      setLoadingGuardar(false);
    }
  };

  const guardarCursoPaso = async () => {
    if (!idCurso) {
      throw new Error("Primero debes cargar un curso por ID.");
    }
    if (!formData.Nombre.trim()) {
      throw new Error("El nombre del curso es obligatorio.");
    }
    if (!formData.Descripcion.trim()) {
      throw new Error("La descripción del curso es obligatoria.");
    }
    if (!Number.isInteger(formData.PorcentajeAprobacion) || formData.PorcentajeAprobacion < 1 || formData.PorcentajeAprobacion > 100) {
      throw new Error("El porcentaje de aprobación debe estar entre 1 y 100.");
    }
    if (!Number.isInteger(formData.CantidadPreguntas) || formData.CantidadPreguntas < 1 || formData.CantidadPreguntas > 25) {
      throw new Error("La cantidad de preguntas debe estar entre 1 y 25.");
    }

    const payload = {
      Nombre: formData.Nombre.trim(),
      Descripcion: formData.Descripcion.trim(),
      Estado: formData.Estado,
      PorcentajeAprobacion: formData.PorcentajeAprobacion,
      CantidadPreguntas: formData.CantidadPreguntas,
    };
    if (removedCover && !coverFile) {
      payload.ImagenPortada = null;
    }
    await updateCurso(idCurso, payload);
    if (coverFile) {
      const res = await uploadCursoPortada(idCurso, coverFile);
      const newPath = res?.ImagenPortada;
      if (newPath) {
        setFormData((prev) => ({ ...prev, ImagenPortada: newPath }));
        setPreview(newPath);
      }
      revokeLocalPreviewUrl();
      setCoverFile(null);
      setRemovedCover(false);
      setFileName("");
    } else if (removedCover) {
      setRemovedCover(false);
    }
  };

  const guardarSeccionesPaso = async () => {
    if (!idCurso) {
      throw new Error("Primero debes cargar un curso por ID.");
    }
  };

  const handleContinuarSecciones = async () => {
    try {
      setLoadingGuardar(true);
      await guardarCursoPaso();
      setStep(2);
      Swal.fire("Guardado", "Cambios del curso guardados correctamente.", "success");
    } catch (error) {
      Swal.fire("Error", error?.response?.data?.message || error?.message || "No se pudieron guardar los cambios del curso.", "error");
    } finally {
      setLoadingGuardar(false);
    }
  };

  const handleContinuarPreguntas = async () => {
    try {
      setLoadingGuardar(true);
      await guardarSeccionesPaso();
      await recargarSecciones(idCurso);
      setStep(3);
      Swal.fire("Guardado", "Secciones guardadas correctamente.", "success");
    } catch (error) {
      Swal.fire("Error", error?.response?.data?.message || error?.message || "No se pudieron guardar las secciones.", "error");
    } finally {
      setLoadingGuardar(false);
    }
  };

  const openCrearPreguntaModal = () => {
    setPreguntaSeleccionada(null);
    setPreguntaModalOpen(true);
  };

  const openEditarPreguntaModal = (preg) => {
    setPreguntaSeleccionada(preg);
    setPreguntaModalOpen(true);
  };

  const handleEliminarPregunta = async (pregunta) => {
    const estadoPregunta = Number(pregunta?.estado ?? 1);
    const esActiva = estadoPregunta === 1;

    if (esActiva) {
      const confirm = await Swal.fire({
        title: "Eliminar pregunta",
        text: "¿Deseas eliminar esta pregunta?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });
      if (!confirm.isConfirmed) return;
    }

    try {
      setLoadingGuardar(true);
      await deletePregunta(pregunta?.idPregunta);
      await cargarPreguntasCurso(idCurso);
      Swal.fire(esActiva ? "Eliminada" : "Habilitada", esActiva ? "Pregunta eliminada correctamente." : "Pregunta habilitada correctamente.", "success");
    } catch (error) {
      Swal.fire("Error", getApiErrorMessage(error, esActiva ? "No se pudo eliminar la pregunta." : "No se pudo habilitar la pregunta."), "error");
    } finally {
      setLoadingGuardar(false);
    }
  };

  const preguntasFiltradas = preguntas.filter((preg) => {
    const query = preguntasSearch.trim().toLowerCase();
    const coincideTexto =
      !query ||
      String(preg?.pregunta || "")
        .toLowerCase()
        .includes(query) ||
      String(preg?.descripcion || "")
        .toLowerCase()
        .includes(query);

    const estadoPregunta = Number(preg?.estado ?? 1);
    const coincideEstado = soloPreguntasActivas ? estadoPregunta === 1 : estadoPregunta !== 1;

    return coincideTexto && coincideEstado;
  });

  const totalPreguntasPages = Math.max(1, Math.ceil(preguntasFiltradas.length / PREGUNTAS_PAGE_SIZE));
  const preguntasFiltradasPaginadas = preguntasFiltradas.slice(
    (preguntasPage - 1) * PREGUNTAS_PAGE_SIZE,
    preguntasPage * PREGUNTAS_PAGE_SIZE
  );

  useEffect(() => {
    setPreguntasPage(1);
  }, [preguntasSearch, soloPreguntasActivas, preguntas.length]);

  useEffect(() => {
    if (preguntasPage > totalPreguntasPages) {
      setPreguntasPage(totalPreguntasPages);
    }
  }, [preguntasPage, totalPreguntasPages]);

  return (
    <Card sx={{ backgroundColor: "#fff", borderRadius: 2, boxShadow: "none" }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <EditIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">Editar curso existente</Typography>
        </Box>

        {loadingBuscar && (
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary">
              Cargando información del curso...
            </Typography>
          </Box>
        )}

        {cursoCargado && (
          <Stepper activeStep={step - 1} sx={{ mb: 3 }}>
            {["Curso", "Secciones", "Preguntas"].map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        <Box component="form" onSubmit={handleGuardarCambios}>
          {step === 1 && <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre del curso"
                name="Nombre"
                value={formData.Nombre}
                onChange={handleChange}
                required
                disabled={!cursoCargado}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                name="Descripcion"
                value={formData.Descripcion}
                onChange={handleChange}
                multiline
                rows={4}
                required
                disabled={!cursoCargado}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Porcentaje de aprobación (%)"
                name="PorcentajeAprobacion"
                type="number"
                inputProps={{ min: 1, max: 100 }}
                value={formData.PorcentajeAprobacion}
                onChange={handleChange}
                required
                disabled={!cursoCargado}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cantidad de preguntas"
                name="CantidadPreguntas"
                type="number"
                inputProps={{ min: 1, max: 25 }}
                value={formData.CantidadPreguntas}
                onChange={handleChange}
                required
                disabled={!cursoCargado}
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  border: "1px dashed #ccc",
                  borderRadius: 2,
                  textAlign: "center",
                  bgcolor: "#fafafa",
                  opacity: cursoCargado ? 1 : 0.6,
                }}
              >
                {coverDisplaySrc ? (
                  <Box>
                    <Box
                      component="img"
                      src={coverDisplaySrc}
                      alt="Vista previa"
                      sx={{
                        maxHeight: 160,
                        maxWidth: "100%",
                        objectFit: "contain",
                        borderRadius: 1,
                        mb: 1,
                      }}
                    />
                    <Typography variant="caption" display="block">
                      {fileName || "Imagen actual del curso"}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => {
                        revokeLocalPreviewUrl();
                        setCoverFile(null);
                        setRemovedCover(true);
                        setPreview("");
                        setFileName("");
                        setFormData((prev) => ({ ...prev, ImagenPortada: "" }));
                      }}
                      sx={{ mt: 1 }}
                      disabled={!cursoCargado}
                    >
                      Quitar imagen
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <ImageIcon
                      sx={{ fontSize: 48, color: "action.disabled", mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Imagen de portada
                    </Typography>
                    <input
                      accept="image/*"
                      id="upload-cover-edit"
                      type="file"
                      style={{ display: "none" }}
                      onChange={handleImageChange}
                      disabled={!cursoCargado}
                    />
                    <label htmlFor="upload-cover-edit">
                      <Button
                        variant="outlined"
                        component="span"
                        disabled={!cursoCargado}
                      >
                        Seleccionar imagen
                      </Button>
                    </label>
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth disabled={!cursoCargado}>
                <InputLabel id="estado-edit-label">Estado</InputLabel>
                <Select
                  labelId="estado-edit-label"
                  name="Estado"
                  value={formData.Estado}
                  label="Estado"
                  onChange={handleChange}
                >
                  <MenuItem value={1}>Activo</MenuItem>
                  <MenuItem value={0}>Inactivo</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box mt={2} display="flex" gap={2} justifyContent="flex-end">
                <Button variant="outlined" onClick={resetForm}>
                  Limpiar
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!cursoCargado}
                  onClick={handleContinuarSecciones}
                >
                  {loadingGuardar ? "Guardando..." : "Guardar y continuar"}
                </Button>
              </Box>
            </Grid>
          </Grid>}

          {step === 2 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  label="Nombre de sección"
                  value={seccionForm.nombre}
                  onChange={(e) => setSeccionForm((prev) => ({ ...prev, nombre: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Orden"
                  type="number"
                  value={seccionForm.orden}
                  onChange={(e) => setSeccionForm((prev) => ({ ...prev, orden: Number(e.target.value) || 1 }))}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={seccionForm.estado}
                    label="Estado"
                    onChange={(e) => setSeccionForm((prev) => ({ ...prev, estado: Number(e.target.value) }))}
                  >
                    <MenuItem value={1}>Activo</MenuItem>
                    <MenuItem value={0}>Inactivo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={seccionForm.idSeccion ? <BorderColorIcon /> : <AddIcon />}
                  disabled={loadingSeccion}
                  onClick={handleGuardarSeccion}
                >
                  {loadingSeccion
                    ? "Guardando..."
                    : seccionForm.idSeccion
                    ? "Editar"
                    : "Agregar"}
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 1.5,
                    border: "1px dashed #ccc",
                    borderRadius: 2,
                    bgcolor: "#fafafa",
                  }}
                >
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Archivo PDF de la sección
                  </Typography>
                  <input
                    accept=".pdf,application/pdf"
                    id="seccion-pdf-edit"
                    type="file"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.type !== "application/pdf") {
                        Swal.fire("Error", "Solo se permiten archivos PDF.", "error");
                        return;
                      }
                      setSeccionForm((prev) => ({
                        ...prev,
                        archivo: file,
                        archivoNombre: file.name,
                      }));
                      e.target.value = "";
                    }}
                  />
                  <label htmlFor="seccion-pdf-edit">
                    <Button variant="outlined" component="span" startIcon={<PictureAsPdfIcon />} size="small">
                      {seccionForm.archivo?.name || "Seleccionar PDF"}
                    </Button>
                  </label>
                  {(seccionForm.archivo || seccionForm.archivoNombre) && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      {seccionForm.archivo
                        ? `Nuevo archivo: ${seccionForm.archivo.name}`
                        : `Archivo actual: ${seccionForm.archivoNombre}`}
                    </Typography>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <List dense sx={{ bgcolor: "#fafafa", borderRadius: 1 }}>
                  {secciones.map((sec, idx) => (
                    <ListItem
                      key={`sec-${idx}`}
                      sx={
                        sec.estado === 0
                          ? {
                              backgroundColor: "#ffebee",
                              borderLeft: "3px solid #ef9a9a",
                              borderRadius: 1,
                              mb: 0.5,
                            }
                          : undefined
                      }
                      secondaryAction={
                        <Box display="flex" gap={1}>
                          {resolveFileUrl(sec.archivoUrl || sec.archivoNombre) && (
                            <IconButton
                              edge="end"
                              component="a"
                              href={resolveFileUrl(sec.archivoUrl || sec.archivoNombre)}
                              target="_blank"
                              rel="noreferrer"
                              title="Descargar PDF"
                              aria-label="Descargar PDF"
                            >
                              <PictureAsPdfIcon />
                            </IconButton>
                          )}
                          <IconButton
                            edge="end"
                            onClick={() => {
                              setSeccionForm({
                                idSeccion: sec.idSeccion || null,
                                nombre: sec.nombre,
                                orden: sec.orden,
                                estado: sec.estado,
                                archivo: sec.archivo || null,
                                archivoNombre: sec.archivoNombre || "",
                                archivoUrl: sec.archivoUrl || "",
                              });
                            }}
                          >
                            <BorderColorIcon />
                          </IconButton>
                          <IconButton edge="end" onClick={() => handleEliminarSeccion(sec)}>
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={sec.nombre}
                        secondary={`Orden: ${sec.orden} · Estado: ${sec.estado === 1 ? "Activo" : "Inactivo"} · Archivo: ${sec.archivo?.name || sec.archivoNombre || "Sin archivo"}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between">
                  <Button variant="outlined" onClick={() => setStep(1)}>Volver a curso</Button>
                  <Button variant="contained" onClick={handleContinuarPreguntas} disabled={loadingGuardar}>
                    {loadingGuardar ? "Guardando..." : "Guardar y continuar a preguntas"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}

          {step === 3 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} mb={1} gap={1} flexWrap="wrap">
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Preguntas del curso ({preguntas.length})
                  </Typography>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={openCrearPreguntaModal} sx={{ width: { xs: "100%", sm: "auto" } }}>
                    Nueva pregunta
                  </Button>
                </Box>
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    border: "1px solid #E5E7EB",
                    borderRadius: 2,
                    backgroundColor: "#FAFAFB",
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Buscar por pregunta o descripción"
                        value={preguntasSearch}
                        onChange={(e) => setPreguntasSearch(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box display="flex" alignItems="center" justifyContent={{ xs: "flex-start", md: "flex-end" }} gap={1}>
                        <Typography variant="body2" color="text.secondary">
                          Solo activas
                        </Typography>
                        <Switch checked={soloPreguntasActivas} onChange={(e) => setSoloPreguntasActivas(e.target.checked)} />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
                {loadingPreguntas ? (
                  <Typography variant="body2" color="text.secondary">
                    Cargando preguntas...
                  </Typography>
                ) : preguntasFiltradas.length === 0 ? (
                  <Box sx={{ p: 3, border: "1px dashed #ddd", borderRadius: 2, textAlign: "center" }}>
                    <Typography color="text.secondary">
                      {preguntas.length === 0
                        ? "Este curso aún no tiene preguntas."
                        : "No hay preguntas que coincidan con los filtros."}
                    </Typography>
                  </Box>
                ) : (
                  <Box display="flex" flexDirection="column" gap={2}>
                    {preguntasFiltradasPaginadas.map((preg, idx) => {
                      const tipoLabel = preg?.tipo || "Sin tipo";
                      const esInactiva = Number(preg?.estado ?? 1) === 0;
                      const indiceGlobal = (preguntasPage - 1) * PREGUNTAS_PAGE_SIZE + idx;
                      const rangoMin = Array.isArray(preg?.rango) ? preg.rango[0] : 0;
                      const rangoMax = Array.isArray(preg?.rango) ? preg.rango[1] : 100;
                      return (
                        <Card
                          key={`preg-card-${preg.idPregunta || indiceGlobal}`}
                          variant="outlined"
                          sx={{
                            borderRadius: 2,
                            borderColor: esInactiva ? "#F5C2C7" : "#E5E7EB",
                            backgroundColor: esInactiva ? "#FFF5F5" : "#FCFCFD",
                          }}
                        >
                          <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} mb={1} gap={1} flexWrap="wrap">
                              <Typography variant="caption" color="text.secondary">
                                Pregunta {indiceGlobal + 1} · {tipoLabel} · {esInactiva ? "Inactiva" : "Activa"}
                              </Typography>
                              <Box display="flex" gap={1}>
                                <IconButton size="small" onClick={() => openEditarPreguntaModal(preg)}>
                                  <BorderColorIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEliminarPregunta(preg)}
                                  title={esInactiva ? "Habilitar pregunta" : "Eliminar pregunta"}
                                  aria-label={esInactiva ? "Habilitar pregunta" : "Eliminar pregunta"}
                                >
                                  {esInactiva ? <CheckCircleOutlineIcon fontSize="small" /> : <DeleteIcon fontSize="small" />}
                                </IconButton>
                              </Box>
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {preg.pregunta}
                            </Typography>
                            {preg.descripcion && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {preg.descripcion}
                              </Typography>
                            )}
                            <Typography variant="caption" sx={{ mt: 0.5, display: "block", color: "text.secondary" }}>
                              Edad permitida: {rangoMin} - {rangoMax} años
                            </Typography>
                            <Divider sx={{ my: 1.5, borderColor: "#ECEFF3" }} />
                            <Box display="flex" flexDirection="column" gap={0.75}>
                              {(preg.opciones || []).map((op, opIdx) => (
                                <Typography
                                  key={`op-view-${preg.idPregunta || idx}-${opIdx}`}
                                  variant="body2"
                                  sx={{
                                    color: op.correcta ? "#01d711" : "text.primary",
                                    fontWeight: op.correcta ? 600 : 400,
                                  }}
                                >
                                  {op.correcta ? "● " : "○ "}
                                  {op.respuesta}
                                </Typography>
                              ))}
                              {(!preg.opciones || preg.opciones.length === 0) && (
                                <Typography variant="body2" color="text.secondary">
                                  Tipo: {preg.tipo}
                                </Typography>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                )}
                {preguntasFiltradas.length > PREGUNTAS_PAGE_SIZE && (
                  <Box mt={2} display="flex" justifyContent="center">
                    <Pagination
                      page={preguntasPage}
                      count={totalPreguntasPages}
                      shape="rounded"
                      onChange={(_, value) => setPreguntasPage(value)}
                    />
                  </Box>
                )}
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" gap={1} flexWrap="wrap">
                  <Button variant="outlined" onClick={() => setStep(2)} sx={{ width: { xs: "100%", sm: "auto" } }}>Volver a secciones</Button>
                  <Button variant="contained" color="primary" onClick={() => onSuccess?.()} sx={{ width: { xs: "100%", sm: "auto" } }}>
                    Volver al listado de cursos
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}

          <Dialog
            open={preguntaModalOpen}
            onClose={() => {
              setPreguntaModalOpen(false);
              setPreguntaSeleccionada(null);
            }}
            fullWidth
            fullScreen={isMobile}
            maxWidth="lg"
          >
            <DialogContent sx={{ pt: 2, px: 0 }}>
              <AddCourseQuestionsForm
                idCurso={idCurso}
                nombreCurso={formData.Nombre}
                mode="single"
                initialPregunta={preguntaSeleccionada}
                onCancel={() => {
                  setPreguntaModalOpen(false);
                  setPreguntaSeleccionada(null);
                }}
                onSuccess={async () => {
                  await cargarPreguntasCurso(idCurso);
                  setPreguntaModalOpen(false);
                  setPreguntaSeleccionada(null);
                }}
              />
            </DialogContent>
          </Dialog>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EditCourseForm;
